import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroupDefinition } from 'components';
import { asyncScheduler, Observable, of } from 'rxjs';
import { first, map, observeOn, take } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';

@Component({
  standalone: false,
  selector: 'app-frequency-code-dialog',
  templateUrl: './frequency-code-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./frequency-code-dialog.component.scss']
})
export class FrequencyCodeDialogComponent implements OnInit {
  formGroup = new UntypedFormGroup({});
  formInitialized = false;
  saving = false;
  CODE_TYPE = 'FrequencyCode';
  frequencyCodeId;
  viewOnly = false;

  formDefinitions: FormGroupDefinition[];
  getFormDefinitions(isEdit: boolean): FormGroupDefinition[] {
    return [
      {
        hideTitle: true,
        controls: [
          {
            label: 'Code',
            name: 'code',
            isReadOnly: isEdit,
            type: 'text',
            class: 'form-span-10',
            validators: [Validators.required, Validators.maxLength(4)]
          },
          {
            label: 'Active',
            name: 'active',
            type: 'checkbox',
            class: 'form-span-2',
            initial: true,
            validators: Validators.required
          },
          {
            label: 'Description',
            name: 'name',
            type: 'text',
            class: 'form-span-12',
            validators: Validators.required
          }
        ]
      }
    ];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private injector: Injector,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<FrequencyCodeDialogComponent>,
    private service: CodeWrapperService
  ) {
    this.frequencyCodeId = data?.frequencyCodeId;
    const isEdit = this.frequencyCodeId && this.frequencyCodeId !== '';
    this.viewOnly = data?.viewOnly;
    this.formDefinitions = this.getFormDefinitions(isEdit);
  }

  ngOnInit(): void {
    this.setupForm();

    if (this.frequencyCodeId && this.frequencyCodeId !== '') {
      this.service
        .apiV1CodeCodetypeIdGet(this.CODE_TYPE, this.frequencyCodeId)
        .pipe(first())
        .subscribe((result) => {
          this.formGroup.patchValue(result);
          this.formGroup.get('active').patchValue(!result.inactive);
        });
    }
  }

  setupForm() {
    this.formDefinitions.forEach((sc) => {
      sc.controls.forEach((control) => {
        if (control.type !== 'empty' && control.type !== 'label') {
          this.formGroup.addControl(control.name, new UntypedFormControl(control.initial ?? '', control.validators));
        }
      });
    });
    this.formInitialized = true;
  }

  save() {
    if (this.formGroup.valid && this.saving === false) {
      this.saving = true;

      const formData = {
        code: this.formGroup.get('code').value,
        name: this.formGroup.get('name').value,
        inactive: this.formGroup.get('active').value === true ? false : true
      };

      let saveObservable: Observable<any>;
      let notification;

      if (this.frequencyCodeId) {
        notification = 'Frequency Code Updated';
        saveObservable = this.service
          .apiV1CodeCodetypePatchIdPatch(this.CODE_TYPE, this.frequencyCodeId, [
            {
              value: formData.name,
              path: 'name',
              op: 'add'
            },

            {
              value: formData.code,
              path: 'code',
              op: 'add'
            },

            {
              value: formData.inactive,
              path: 'inactive',
              op: 'add'
            }
          ])
          .pipe(
            map((x: any) => {
              x;
            })
          );
      } else {
        notification = 'Frequency Code Added';
        saveObservable = this.service.apiV1CodeCodetypeAddPost(this.CODE_TYPE, formData).pipe(map((x: any) => x));
      }

      saveObservable
        .pipe(take(1), observeOn(asyncScheduler))
        .subscribe(
          (response) => {
            this.dialogRef.close(true);
            this.notificationService.success(notification);
          },
          (err) => {
            this.notificationService.error('Error Saving Frequency Code');
            this.saving = false;
          }
        );
    }
  }
}
